import { ROLE_TOKENS } from "../lib/ownership";
import { X, FunnelSimple } from "@phosphor-icons/react";

/**
 * Page-local ownership filter. ALWAYS defaults to "all" (we don't persist).
 *
 *   <OwnerFilter
 *     value="all"|"staff"|"bookkeeper"|"owner"|"done"
 *     onChange={fn}
 *     counts={{ all: 28, staff: 4, bookkeeper: 12, owner: 1, done: 11 }}
 *     viewerRole="staff"   // used to label the "yours" button
 *     showDone             // optional — show a "Done" pill
 *     hideOwner            // optional — hide owner button if not relevant
 *   />
 */
export default function OwnerFilter({
  value = "all",
  onChange,
  counts = {},
  viewerRole,
  showDone = false,
  hideOwner = false,
  testid = "owner-filter",
}) {
  const buttons = [
    { key: "all", label: "All", color: "#0A0A0A" },
    { key: "staff", label: viewerRole === "staff" ? "Yours" : "Admin", color: ROLE_TOKENS.staff.color },
    { key: "bookkeeper", label: viewerRole === "bookkeeper" ? "Yours" : "Bookkeeper", color: ROLE_TOKENS.bookkeeper.color },
  ];
  if (!hideOwner) {
    buttons.push({ key: "owner", label: viewerRole === "owner" ? "Yours" : "Owner", color: ROLE_TOKENS.owner.color });
  }
  if (showDone) {
    buttons.push({ key: "done", label: "Done", color: "#525252" });
  }

  const active = value && value !== "all";
  const total = counts.all ?? 0;
  const visible = active ? (counts[value] ?? 0) : total;

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1.5">
        <FunnelSimple size={12} weight="duotone" className="text-[#525252] mr-0.5" />
        {buttons.map((b) => {
          const isActive = value === b.key;
          const c = counts[b.key];
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => onChange?.(b.key)}
              data-testid={`${testid}-${b.key}`}
              className="text-[11px] px-2 py-1 border transition-all flex items-center gap-1.5"
              style={{
                color: isActive ? "#FFFFFF" : b.color,
                backgroundColor: isActive ? b.color : "#FFFFFF",
                borderColor: b.color,
              }}
            >
              <span className="font-semibold tracking-wider uppercase">{b.label}</span>
              {c !== undefined && (
                <span className={`text-[10px] ${isActive ? "opacity-90" : "opacity-70"}`}>
                  {c}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {active && (
        <div
          data-testid={`${testid}-active-banner`}
          className="mt-2 flex items-center gap-2 text-[11px] bg-[#FEF3C7] border-l-2 border-[#F59E0B] px-2.5 py-1.5"
        >
          <span className="text-[#92400E]">
            Showing <b>{visible}</b> of <b>{total}</b> · filter is on
          </span>
          <button
            type="button"
            onClick={() => onChange?.("all")}
            className="ml-auto text-[#0A0A0A] underline decoration-dotted hover:no-underline flex items-center gap-1"
            data-testid={`${testid}-show-all`}
          >
            <X size={10} weight="bold" /> Show all
          </button>
        </div>
      )}
    </div>
  );
}
